import type { JobStatus, JobStep, RiskLevel } from "@clarent/scheduling";

/**
 * Risk labels and colours from the Backplan design system. On track and Tight are tint
 * pills; At risk and Can't make it are solid.
 */
export const RISK_META: Record<RiskLevel, { label: string; pill: string; dot: string }> = {
  ok: { label: "On track", pill: "bg-ok-tint text-ok-text", dot: "bg-ok" },
  tight: { label: "Tight", pill: "bg-tight-tint text-tight-text", dot: "bg-tight" },
  at_risk: { label: "At risk", pill: "bg-risk text-white", dot: "bg-white" },
  impossible: { label: "Can't make it", pill: "bg-late text-white", dot: "bg-white" },
};

/** At risk and Can't make it turn dates red on cards. */
export function isLate(risk: RiskLevel): boolean {
  return risk === "at_risk" || risk === "impossible";
}

/** `marker` is the column colour from the design system's job stages. `drop` is what the drop zone says. */
export const STATUS_META: Record<JobStatus, { label: string; marker: string; drop: string }> = {
  quoted: { label: "Quoted", marker: "bg-quoted", drop: "Drop to move back to quoted" },
  scheduled: { label: "Scheduled", marker: "bg-brand", drop: "Drop to schedule" },
  in_progress: { label: "In progress", marker: "bg-paint", drop: "Drop to mark as started" },
  at_post_process: { label: "At post-process", marker: "bg-heat", drop: "Drop to send to post-process" },
  complete: { label: "Complete", marker: "bg-ok", drop: "Drop to mark as complete" },
};

export const STATUS_ORDER: JobStatus[] = ["quoted", "scheduled", "in_progress", "at_post_process", "complete"];

/** The drawer's primary action: the usual next stage, named for what happens. */
export function nextStage(status: JobStatus, steps: readonly JobStep[]): { status: JobStatus; label: string } | null {
  switch (status) {
    case "quoted":
      return { status: "scheduled", label: "Schedule job" };
    case "scheduled":
      return { status: "in_progress", label: "Mark as started" };
    case "in_progress":
      return steps.some((s) => s.type === "post_process")
        ? { status: "at_post_process", label: "Send to post-process" }
        : { status: "complete", label: "Mark as complete" };
    case "at_post_process":
      // Machining after the outside work (e.g. grind after heat treat) means it comes back to the shop first.
      return steps.at(-1)?.type === "machining"
        ? { status: "in_progress", label: "Mark as back in the shop" }
        : { status: "complete", label: "Mark as complete" };
    case "complete":
      return null;
  }
}
